// Zodiacal light — the faint wedge of sunlight scattered by the
// interplanetary dust cloud that hugs the ecliptic plane. From inside
// the system it reads as a soft warm band along the ecliptic that
// brightens toward the sun (the pre-dawn "false dawn" astronomers and
// astrophotographers know); a much fainter backscatter bump sits at
// the anti-solar point (the gegenschein). Both are modelled here as a
// single additive disc in the ecliptic plane with an elongation-
// dependent phase function, deliberately a few photographic stops
// below everything else in the scene.

import * as THREE from 'three';

const zodiacalVertexShader = `
  varying vec3 vWorldPosition;
  varying float vRadial;
  uniform float uDiscRadius;

  void main() {
    vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
    vRadial = length(position.xy) / uDiscRadius;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const zodiacalFragmentShader = `
  varying vec3 vWorldPosition;
  varying float vRadial;
  uniform float uIntensity;

  void main() {
    // Radial falloff of dust column brightness. The real zodiacal
    // cloud's surface brightness drops roughly as elongation^-2.3
    // near the sun; we approximate against the disc radius with a
    // steep power plus an inner fade so the sun's own glow sprite
    // owns the innermost region.
    float radial = pow(max(1.0 - vRadial, 0.0), 2.6);
    float innerFade = smoothstep(0.015, 0.06, vRadial);

    // Elongation: angle between the view ray to this dust column and
    // the view ray to the sun (origin). Forward scattering brightens
    // small elongations; the gegenschein is a gentle backscatter bump
    // around 180 degrees.
    vec3 toFrag = normalize(vWorldPosition - cameraPosition);
    vec3 toSun = normalize(-cameraPosition);
    float cosElong = clamp(dot(toFrag, toSun), -1.0, 1.0);
    float forward = pow(max(cosElong, 0.0), 3.0) * 0.85 + 0.15;
    float gegenschein = pow(max(-cosElong, 0.0), 24.0) * 0.35;
    float phase = forward + gegenschein;

    // Outer rim fade so the disc edge never shows as a circle.
    float rimFade = 1.0 - smoothstep(0.78, 1.0, vRadial);

    float brightness = radial * innerFade * phase * rimFade * uIntensity;
    // Sunlight scattered by micron dust is slightly warm white.
    vec3 tint = vec3(1.0, 0.94, 0.84);
    gl_FragColor = vec4(tint * brightness, brightness);
  }
`;

export function createZodiacalLight(options = {}) {
  // Default disc reaches a bit past Mars's orbit — the real cloud's
  // visible brightness concentrates inside ~3 AU.
  const discRadius = options.discRadius || 300;
  const geometry = new THREE.CircleGeometry(discRadius, 96);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uDiscRadius: { value: discRadius },
      uIntensity: { value: options.intensity != null ? options.intensity : 0.16 }
    },
    vertexShader: zodiacalVertexShader,
    fragmentShader: zodiacalFragmentShader,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.rotation.x = -Math.PI / 2; // circle XY plane -> ecliptic XZ plane
  mesh.name = 'zodiacalLight';
  mesh.renderOrder = 4; // under the sun glow sprite (5) and atmospheres (10)
  // Never a click/hover target.
  mesh.raycast = () => {};
  return mesh;
}

// Per-frame visibility ramp: full strength while the camera is inside
// the inner system, gone by the time the whole system is in frame so
// overview shots and remote systems stay clean.
export function updateZodiacalLight(mesh, camera, baseIntensity = 0.16) {
  if (!mesh) return;
  const camDist = camera.position.length();
  const fade = 1.0 - THREE.MathUtils.smoothstep(camDist, 450, 900);
  mesh.material.uniforms.uIntensity.value = baseIntensity * fade;
  mesh.visible = fade > 0.002;
}
